/**
 * @file compute-throttle-delay.ts
 * @description Calcul centralisé du délai d'attente avant un retry sur throttle 429.
 *
 * Deux régimes (cf. standard 429 unifié du SDK) :
 *
 *  1. **Retry-After serveur (autoritaire).** Lu depuis DEUX headers, dans cet ordre :
 *      - `Retry-After`        — en **secondes** (entier)
 *      - `x-ms-retry-after-ms`— en **millisecondes** (header Microsoft Graph)
 *     La valeur est **clampée à [0, MAX_RETRY_AFTER_MS]** pour neutraliser une valeur
 *     négative (retry immédiat « négatif ») ou aberrante (blocage long ×N).
 *     Ce délai N'A PAS de jitter : la valeur serveur fait foi.
 *
 *  2. **Fallback — backoff exponentiel + jitter** quand aucun header exploitable :
 *     `min(baseDelay * 2^attempt, maxDelay)` puis jitter ±20%
 *     (`delay * (1 + (random()*0.4 - 0.2))`). `random` est injectable pour les tests.
 *
 * Fonction PURE (aucun effet de bord, aucun log) → testable sans réseau ni timers.
 */

/** Options de calcul du délai (toutes optionnelles, valeurs par défaut = standard SDK). */
export type ComputeThrottleDelayOptions = {
    /** Délai de base du backoff exponentiel (ms). Défaut : 1000. */
    baseDelayMs?: number;
    /** Plafond du backoff exponentiel (ms). Défaut : 16000. */
    maxDelayMs?: number;
    /** Plafond appliqué à la valeur Retry-After serveur (ms). Défaut : 30000. */
    maxRetryAfterMs?: number;
    /**
     * Applique (ou non) le jitter ±20% sur le fallback exponentiel. Défaut : true.
     * (Permet de reproduire à l'identique un site historique sans jitter.)
     */
    jitter?: boolean;
    /** Source d'aléa injectable pour le jitter (tests déterministes). Défaut : Math.random. */
    random?: () => number;
};

const DEFAULTS = {
    baseDelayMs: 1000,
    maxDelayMs: 16000,
    maxRetryAfterMs: 30000,
    jitter: true,
};

/**
 * Plancher (ms) appliqué à la valeur Retry-After serveur. Neutralise un
 * `Retry-After: 0` (ou négatif) qui, sinon, déclencherait des retries immédiats
 * dos-à-dos (rafale). On respecte l'intention « retente bientôt » sans busy-loop.
 */
const MIN_RETRY_AFTER_MS = 100;

/**
 * Source de headers acceptée : soit un objet façon `Headers` (avec `.get()`, ex.
 * `Response.headers`), soit une simple map clé→valeur (ex. sous-réponse Graph `/$batch`,
 * dont les headers sont un objet JSON plat). La lecture est **insensible à la casse**.
 */
export type HeaderSource =
    | Pick<Headers, "get">
    | Record<string, unknown>
    | null
    | undefined;

/** Lit un header (insensible à la casse) depuis un objet Headers OU une map plate. */
function readHeader(headers: HeaderSource, name: string): string | null {
    if (!headers) {
        return null;
    }

    // Cas Headers natif (Response.headers) : .get() gère déjà l'insensibilité à la casse.
    if (typeof (headers as Pick<Headers, "get">).get === "function") {
        return (headers as Pick<Headers, "get">).get(name);
    }

    // Cas map plate (sous-réponse $batch) : recherche insensible à la casse.
    const target = name.toLowerCase();
    for (const [key, value] of Object.entries(
        headers as Record<string, unknown>,
    )) {
        if (key.toLowerCase() !== target) {
            continue;
        }
        // Valeur de header attendue primitive (string pour les sous-réponses Graph/$batch).
        // On ne stringifie jamais un objet (qui produirait "[object Object]").
        if (typeof value === "string") {
            return value;
        }
        if (typeof value === "number" || typeof value === "boolean") {
            return String(value);
        }
        return null;
    }
    return null;
}

/**
 * Lit un éventuel délai Retry-After depuis les headers d'une réponse.
 * @returns le délai en ms (déjà clampé à [0, maxRetryAfterMs]) ou `null` si absent/illisible.
 */
/** Clampe une valeur Retry-After (ms) sur [MIN_RETRY_AFTER_MS, maxRetryAfterMs]. */
function clampRetryAfterMs(ms: number, maxRetryAfterMs: number): number {
    return Math.min(Math.max(ms, MIN_RETRY_AFTER_MS), maxRetryAfterMs);
}

function readRetryAfterMs(
    headers: HeaderSource,
    maxRetryAfterMs: number,
): number | null {
    // 1) x-ms-retry-after-ms (millisecondes) — header Microsoft Graph, prioritaire car plus précis.
    const msHeader = Number.parseInt(
        readHeader(headers, "x-ms-retry-after-ms") ?? "",
        10,
    );
    if (Number.isFinite(msHeader)) {
        return clampRetryAfterMs(msHeader, maxRetryAfterMs);
    }

    // 2) Retry-After (secondes).
    const secHeader = Number.parseInt(
        readHeader(headers, "Retry-After") ?? "",
        10,
    );
    if (Number.isFinite(secHeader)) {
        return clampRetryAfterMs(secHeader * 1000, maxRetryAfterMs);
    }

    return null;
}

/**
 * Calcule le délai (ms) à attendre avant la tentative de retry n° `attempt`.
 *
 * @param source  - La source des headers Retry-After. Accepte :
 *                   - une `Response` / `{ headers }` (ex : fetch direct),
 *                   - un objet `Headers`,
 *                   - une map plate `{ "Retry-After": "10" }` (ex : sous-réponse `/$batch`),
 *                   - `null`/`undefined` (→ toujours le fallback exponentiel).
 * @param attempt - Numéro de la tentative de retry (1 = premier retry). Pilote l'exposant.
 * @param opts    - Surcharges optionnelles (cf. {@link ComputeThrottleDelayOptions}).
 * @returns Le délai en millisecondes.
 */
export function computeThrottleDelayMs(
    source: { headers: HeaderSource } | HeaderSource,
    attempt: number,
    opts: ComputeThrottleDelayOptions = {},
): number {
    const baseDelayMs = opts.baseDelayMs ?? DEFAULTS.baseDelayMs;
    const maxDelayMs = opts.maxDelayMs ?? DEFAULTS.maxDelayMs;
    const maxRetryAfterMs = opts.maxRetryAfterMs ?? DEFAULTS.maxRetryAfterMs;
    const jitter = opts.jitter ?? DEFAULTS.jitter;
    const random = opts.random ?? Math.random;

    // Normalise la source vers un HeaderSource : `{ headers }` (Response) ou directement les headers.
    const headers: HeaderSource =
        source && typeof source === "object" && "headers" in source
            ? (source as { headers: HeaderSource }).headers
            : (source as HeaderSource);

    // Régime 1 — Retry-After serveur (autoritaire, pas de jitter).
    const retryAfterMs = readRetryAfterMs(headers, maxRetryAfterMs);
    if (retryAfterMs !== null) {
        return retryAfterMs;
    }

    // Régime 2 — backoff exponentiel plafonné.
    const exponent = Math.max(0, attempt - 1);
    const base = Math.min(baseDelayMs * 2 ** exponent, maxDelayMs);

    if (!jitter) {
        return base;
    }

    // Jitter ±20% : delay * (1 + (random*0.4 - 0.2)). Jamais négatif (random ∈ [0,1)).
    return Math.round(base * (1 + (random() * 0.4 - 0.2)));
}
